'use client';

import { useMemo } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import { urlSearchParamsToQueryString } from '@/modules/common/lib/utils';
import { getLastCategoryName } from '@/modules/common/services/algoliaUtils';
import { sendGTMEvent } from '@/modules/common/services/sendGTMEvent';
import { RefinementType } from '@/modules/cora/models/Algolia.interface';
import { DataLayerFeature } from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { DATA_LAYER_EVENTS } from '@/modules/product/services/data-layer-events/events';
import { Filter } from '@/modules/product/services/filtersTransform';
import { parseFiltersFromUrl } from '@/modules/product/services/routingHelpers';

const excludedFacetAttributes = [
  'attributes.Affixing_System.label',
  'attributes.Brand',
  'attributes.Brand_ID',
  'attributes.Content_for_Filtering.label',
  'attributes.Indoor_And_Outdoor.label',
  'attributes.MSRP_Converted',
  'attributes.MSRP_UOM_Converted.label',
  'categories.en.lvl2',
  'categories.en.lvl3',
  'prices.USD.max',
  'prices.USD.min',
  'searchableAttributes.Resistance_Type.label',
  'attributes.Width',
  'attributes.Construction',
  'attributes.Product_Price_Prefix.label',
  'attributes.Grout_Width',
  'attributes.Size',
  'attributes.finish.label',
  'attributes.wood_grade',
  'attributes.stone_pattern',
  'attributes.format',
  'attributes.feature_icons',
  'attributes.DML_space_application',
];

const includesFacetAttributes = [
  'attributes.available_finishes',
  'attributes.resistance_type',
  'attributes.space_application',
];

const MAX_REFINEMENT_BUTTONS = 5;

type RefineSearchButtonsSimplifiedProps = {
  className?: string;
  facets: Record<string, Record<string, number>> | undefined;
  query: string;
  filtersDefinition: Filter[];
  hitsCount: number;
};

/**
 * Simplified RefineSearchButtons that calculates refinements from facets passed as props.
 * No separate query needed - uses facets from the main search results.
 */
export function RefineSearchButtonsSimplified({
  className,
  facets,
  query,
  filtersDefinition,
  hitsCount,
}: RefineSearchButtonsSimplifiedProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Parse current refinements from URL
  const currentRefinements = useMemo(() => {
    const queryParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });
    const routeState = parseFiltersFromUrl({
      queryParams,
      filtersDefinition,
      separator: '|',
    });

    // Convert routeState to refinementList format (attribute -> values[])
    const refinementList: Record<string, string[]> = {};
    filtersDefinition.forEach(({ slug, attribute }) => {
      const refinementValue = routeState.refinements[slug];
      if (refinementValue && Array.isArray(refinementValue)) {
        refinementList[attribute] = refinementValue;
      }
    });
    return refinementList;
  }, [searchParams, filtersDefinition]);

  // Calculate refinements from facets
  const refinements = useMemo(() => {
    if (!facets || !query.trim() || hitsCount < 12) {
      return [];
    }

    const facetsToFilter = filtersDefinition
      .map((filter) => filter.attribute)
      .filter((attribute) => !excludedFacetAttributes.includes(attribute));

    const refinementList: RefinementType[] = Object.entries(facets)
      .filter(([attribute]) => {
        return facetsToFilter.includes(attribute) || includesFacetAttributes.includes(attribute);
      })
      .reduce<RefinementType[]>((acm, [attribute, facetValues]) => {
        const existingLabels = new Set(acm.map((item) => item.label));

        Object.entries(facetValues)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 2)
          .forEach(([value, count]) => {
            const label = getLastCategoryName(value ?? '');

            // Skip if refinement matches query or is already applied
            if (
              value.toLocaleLowerCase() === query.toLocaleLowerCase() ||
              label.toLocaleLowerCase() === query.toLocaleLowerCase() ||
              currentRefinements[attribute]?.includes(value)
            ) {
              return;
            }
            if (!existingLabels.has(value)) {
              acm.push({
                value,
                count,
                highlighted: label,
                label,
                attribute,
              });
              existingLabels.add(value);
            }
          });

        return acm;
      }, [])
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_REFINEMENT_BUTTONS);

    return refinementList.length > 1 ? refinementList : [];
  }, [facets, query, currentRefinements, filtersDefinition, hitsCount]);

  const handleSelectRefinement = (refinement: RefinementType, index: number) => {
    // Send analytics event
    sendGTMEvent({
      event: DATA_LAYER_EVENTS.search_refinement,
      eventData: {
        feature: DataLayerFeature.Search,
        where: DataLayerWhere.Search,
        type: 'Refine your search',
        name: refinement.label,
        facet: refinement.attribute,
        position: index.toString(),
        search_term: query,
      },
    });

    // Find the filter slug for this attribute
    const filter = filtersDefinition.find((f) => f.attribute === refinement.attribute);
    if (!filter) {
      return;
    }

    // Get current values for this filter from URL
    const currentValues = searchParams.get(filter.slug)?.split('|') || [];

    // Check if already applied
    if (currentValues.includes(refinement.value)) {
      return;
    }

    // Add refinement to URL
    const newValues = [...currentValues, refinement.value];
    const params = new URLSearchParams(searchParams.toString());
    params.set(filter.slug, newValues.join('|'));
    params.delete('page'); // Reset to first page

    router.push(`/search?${urlSearchParamsToQueryString(params)}`);
  };

  if (refinements.length === 0) {
    return null;
  }

  return (
    <section className={className} data-testid="refine-search-buttons">
      <div className="max-w-500" data-testid="refinements-scroll-container">
        <div className="flex gap-2 overflow-x-auto md:flex-wrap" data-testid="refinements-list">
          {refinements.map((refinement, index) => (
            <Button
              key={refinement.value}
              variant="secondary"
              className="shrink-0 whitespace-nowrap bg-gray-100 md:whitespace-normal"
              size="s"
              onClick={() => {
                handleSelectRefinement(refinement, index);
              }}
              data-testid={`refinement-button-${refinement.value}`}
            >
              {refinement.label}
            </Button>
          ))}
        </div>
      </div>
    </section>
  );
}
